import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { listModules, myProgress, myPeerReviewModuleStatus, getModule as fetchModule } from "@/lib/apiClient";
import { CheckCircle2, Clock, ArrowRight, Circle, CircleDot, Download, Trash2, WifiOff, Loader2, Lock } from "lucide-react";
import { useOffline } from "@/contexts/OfflineContext";
import { useI18n } from "@/contexts/I18nContext";
import { downloadModule, removeModule, estimateModuleBytes, formatBytes } from "@/lib/offlineCache";
import { resolveModuleName, resolveModuleIntro, resolveModuleSubtitle } from "@/lib/i18nContent";

// Status helpers: not-started → Red · in-progress → Orange · completed → Green
const statusOf = (mp) => {
  if (mp?.passed) return "done";
  if ((mp?.attempts || 0) > 0) return "progress";
  return "new";
};
const STATUS = {
  done:     { color: "#10B981", bg: "#ECFDF5", border: "#A7F3D0", labelKey: "modules_page.status_done",     icon: CheckCircle2 },
  progress: { color: "#D97706", bg: "#FFF7ED", border: "#FED7AA", labelKey: "modules_page.status_progress", icon: CircleDot },
  new:      { color: "#EF4444", bg: "#FEF2F2", border: "#FECACA", labelKey: "modules_page.status_new",      icon: Circle },
};

// The self-assessment pill above is 3-state (not started/in progress/done).
// Peer Reviewed and Complete are each a single fixed-label pill that only
// swaps between two states — pending (red) and achieved (green) — reusing
// the same red/green tokens as STATUS.new/STATUS.done.
const TRACK = {
  pending:  { color: STATUS.new.color,  bg: STATUS.new.bg,  border: STATUS.new.border,  icon: Circle },
  achieved: { color: STATUS.done.color, bg: STATUS.done.bg, border: STATUS.done.border, icon: CheckCircle2 },
};

const STORAGE_WARN_BYTES = 50 * 1024 * 1024; // 50 MB

export default function Modules() {
  const { t } = useI18n();
  const [modules, setModules] = useState([]);
  const [mods, setMods] = useState({});
  const [betaMode, setBetaMode] = useState(false);
  const { online, downloads, downloadedCodes, totalBytes, refresh } = useOffline();
  const [busyCode, setBusyCode] = useState(null);
  const [progressByCode, setProgressByCode] = useState({});
  const [peerStatusByCode, setPeerStatusByCode] = useState({});

  useEffect(() => {
    Promise.all([listModules(), myProgress(), myPeerReviewModuleStatus()]).then(([ms, p, peer]) => {
      setModules(ms);
      setMods(p?.progress?.modules || {});
      setBetaMode(Boolean(p?.config?.beta_mode));
      const byCode = {};
      for (const m of peer?.modules || []) byCode[m.code] = m;
      setPeerStatusByCode(byCode);
    });
  }, []);

  // Peer Reviewed: every section of the module needs 1 peer review.
  // Complete: self-assessment passed AND peer-reviewed AND a teacher has
  // reviewed and approved a submission for this module — all three
  // independent tracks, matching the three pills rendered below.
  const peerReviewedOf = (code) => Boolean(peerStatusByCode[code]?.complete);
  const teacherReviewedOf = (mp) => Boolean(mp?.teacher_reviewed);
  const isModuleComplete = (mp, code) => Boolean(mp?.passed) && peerReviewedOf(code) && teacherReviewedOf(mp);

  const counts = modules.reduce((acc, m) => { acc[statusOf(mods[m.code])]++; return acc; }, { done: 0, progress: 0, new: 0 });

  // Sequential unlock — module N is unlocked iff N === 1 OR module N-1
  // is passed. SRS v2.0 Section 7 — when Beta mode is on, all modules are
  // unlocked. Matches the dashboard's lock semantics.
  const isUnlocked = (idx) => {
    if (betaMode) return true;
    if (idx === 0) return true;
    const prev = modules[idx - 1];
    return Boolean(mods[prev?.code]?.passed);
  };

  const sizeForCode = (code) => downloads.find((d) => d.code === code)?.size_bytes || 0;

  const handleDownload = async (m) => {
    if (busyCode) return;
    try {
      const detail = await fetchModule(m.code);
      const fullModule = detail.module;
      const est = estimateModuleBytes(fullModule);
      const projected = totalBytes + est;
      const warn = projected > STORAGE_WARN_BYTES
        ? "\n\n" + t("modules_page.dl_warn", { size: formatBytes(STORAGE_WARN_BYTES) })
        : "";
      const message = t("modules_page.dl_confirm", {
        name: resolveModuleName(t, m), est: formatBytes(est), projected: formatBytes(projected),
      }) + warn;
      if (!window.confirm(message)) return;
      setBusyCode(m.code);
      setProgressByCode((p) => ({ ...p, [m.code]: { done: 0, total: 1 } }));
      await downloadModule(fullModule, (p) => {
        setProgressByCode((prev) => ({ ...prev, [m.code]: p }));
      });
      await refresh();
      toast.success(t("modules_page.dl_success", { name: resolveModuleName(t, m) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("modules_page.dl_failed"));
    } finally {
      setBusyCode(null);
      setProgressByCode((prev) => {
        const { [m.code]: _drop, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleRemove = async (m) => {
    if (busyCode) return;
    if (!window.confirm(t("modules_page.remove_confirm", { name: resolveModuleName(t, m) }))) return;
    try {
      setBusyCode(m.code);
      await removeModule(m.code);
      await refresh();
      toast.success(t("modules_page.remove_success", { name: resolveModuleName(t, m) }));
    } catch (e) {
      toast.error(e?.message || t("modules_page.remove_failed"));
    } finally { setBusyCode(null); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="modules-page">
      <Header />
      <main className="flex-1 max-w-[1024px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("modules_page.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("modules_page.title")}</h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl">{t("modules_page.intro")}</p>

        {/* Status legend + offline chip */}
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="status-legend">
          <LegendChip status="done"     count={counts.done}     t={t} />
          <LegendChip status="progress" count={counts.progress} t={t} />
          <LegendChip status="new"      count={counts.new}      t={t} />
          {!online && (
            <span data-testid="offline-chip" className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#FEF2F2] text-[#7F1D1D] border border-[#FECACA]">
              <WifiOff className="w-3.5 h-3.5" /> {t("modules_page.offline_chip", { n: downloads.length })}
            </span>
          )}
          {online && downloads.length > 0 && (
            <span data-testid="downloaded-chip" className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]">
              <Download className="w-3.5 h-3.5" /> {t("modules_page.saved_chip", { n: downloads.length, size: formatBytes(totalBytes) })}
            </span>
          )}
        </div>

        <ol className="mt-10 relative border-l-2 border-dashed border-[#E7E0D2] ml-3">
          {modules.map((m, idx) => {
            const mp = mods[m.code] || {};
            const s = statusOf(mp);
            const st = STATUS[s];
            const Icon = st.icon;
            const isDownloaded = downloadedCodes.has(m.code);
            const dlSize = sizeForCode(m.code);
            const dlProgress = progressByCode[m.code];
            const isBusy = busyCode === m.code;
            const locked = !isUnlocked(idx);
            return (
              <li key={m.code} className="relative pl-8 pb-8 last:pb-2" data-testid={`module-row-${m.code}`} data-status={s} data-locked={locked ? "1" : "0"}>
                <div className="absolute -left-[14px] top-0 w-7 h-7 rounded-full grid place-items-center border-2"
                     style={{ background: locked ? "#F5EFE3" : (s === "done" ? st.color : "#FFFFFF"), borderColor: locked ? "#E7E0D2" : st.color }}>
                  {locked ? <Lock className="w-3.5 h-3.5 text-[#9CA3AF]" />
                    : s === "done"
                      ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <span className="text-[11px] font-semibold" style={{ color: st.color }}>{m.n}</span>}
                </div>
                <div className={`block rounded-2xl border-2 p-5 transition group ${locked ? "bg-[#F5EFE3]/50 border-dashed border-[#E7E0D2] cursor-not-allowed select-none" : "bg-white hover:shadow-[0_10px_30px_-15px_rgba(6,78,59,0.2)]"}`}
                     style={locked ? undefined : { borderColor: st.border }}>
                  <div className="flex items-start gap-4">
                    {locked ? (
                      <div className="flex-1 min-w-0" data-testid={`module-locked-${m.code}`}>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="font-heading text-lg font-medium text-[#9CA3AF]">{t("modules_page.module_label", { n: m.n, name: resolveModuleName(t, m) })}</div>
                          <span className={resolveModuleSubtitle(t, m) === m.name_ar ? "font-arabic text-base text-[#9CA3AF]" : "text-base text-[#9CA3AF]"}>{resolveModuleSubtitle(t, m)}</span>
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#9CA3AF] bg-white border border-[#E7E0D2] rounded-full px-2 py-0.5">
                            <Lock className="w-3 h-3" /> {t("modules.locked")}
                          </span>
                        </div>
                        <div className="text-sm text-[#9CA3AF] mt-1.5">{t("modules.locked_hint", { n: m.n - 1 })}</div>
                      </div>
                    ) : (
                      <Link data-testid={`module-link-${m.code}`} to={`/modules/${m.code}`} className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="font-heading text-lg font-medium text-[#1F2937]">{t("modules_page.module_label", { n: m.n, name: resolveModuleName(t, m) })}</div>
                          <span className={resolveModuleSubtitle(t, m) === m.name_ar ? "font-arabic text-base text-[#4B5563]" : "text-base text-[#4B5563]"}>{resolveModuleSubtitle(t, m)}</span>
                        </div>
                        {/* Status pills get their own row — decoupled from the title's
                            length so a longer module name/subtitle never pushes just
                            one pill onto an orphan line by itself. */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span data-testid={`status-pill-${m.code}`}
                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
                                style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                            <Icon className="w-3 h-3" /> {t(st.labelKey)}
                          </span>
                          <TrackPill
                            testid={`peer-reviewed-pill-${m.code}`}
                            achieved={peerReviewedOf(m.code)}
                            label={t("modules_page.status_peer_reviewed")}
                            hint={t("modules_page.peer_reviewed_hint")}
                          />
                          <TrackPill
                            testid={`complete-pill-${m.code}`}
                            achieved={isModuleComplete(mp, m.code)}
                            label={t("modules_page.status_complete")}
                            hint={t("modules_page.complete_hint")}
                          />
                          {isDownloaded && (
                            <span data-testid={`offline-pill-${m.code}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]">
                              <Download className="w-2.5 h-2.5" /> {formatBytes(dlSize)}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#4B5563] mt-2">{resolveModuleIntro(t, m)}</div>
                        <div className="flex flex-wrap gap-3 mt-3 text-xs">
                          {m.target_seconds > 0 && (
                            <span className="inline-flex items-center gap-1 text-[#4B5563]">
                              <Clock className="w-3.5 h-3.5" /> {t("modules_page.target", { s: m.target_seconds })}
                            </span>
                          )}
                          {mp.attempts > 0 && (
                            <span className="text-[#4B5563]">{t("modules_page.attempts_stat", { n: mp.attempts, best: mp.best_ms ? (mp.best_ms / 1000).toFixed(1) + "s" : "—" })}</span>
                          )}
                        </div>
                      </Link>
                    )}
                    {!locked && (
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        {!isDownloaded ? (
                          <button
                            data-testid={`download-btn-${m.code}`}
                            onClick={() => handleDownload(m)}
                            disabled={isBusy || !online}
                            title={!online ? t("modules_page.dl_title_offline") : t("modules_page.dl_title_online")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#E7E0D2] hover:border-[#064E3B] hover:text-[#064E3B] text-[#4B5563] transition disabled:opacity-50 disabled:cursor-not-allowed">
                            {isBusy
                              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {dlProgress ? `${dlProgress.done}/${dlProgress.total}` : "…"}</>
                              : <><Download className="w-3.5 h-3.5" /> {t("modules_page.save_offline")}</>}
                          </button>
                        ) : (
                          <button
                            data-testid={`remove-btn-${m.code}`}
                            onClick={() => handleRemove(m)}
                            disabled={isBusy}
                            title={t("modules_page.remove_title")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#FECACA] text-[#B45309] hover:border-[#D97706] hover:text-[#7F1D1D] transition disabled:opacity-50">
                            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            {t("modules_page.remove")}
                          </button>
                        )}
                        <ArrowRight className="w-5 h-5 text-[#4B5563] group-hover:text-[#064E3B] transition" />
                      </div>
                    )}
                  </div>
                  {dlProgress && dlProgress.total > 0 && (
                    <div className="mt-3 h-1 bg-[#E7E0D2] rounded-full overflow-hidden" data-testid={`download-progress-${m.code}`}>
                      <div className="h-full bg-[#064E3B] transition-all" style={{ width: `${Math.round(100 * dlProgress.done / dlProgress.total)}%` }} />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </main>
      <Footer />
    </div>
  );
}

function TrackPill({ achieved, label, hint, testid }) {
  const tr = achieved ? TRACK.achieved : TRACK.pending;
  const Icon = tr.icon;
  return (
    <span data-testid={testid} title={hint}
          className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
          style={{ background: tr.bg, color: tr.color, border: `1px solid ${tr.border}` }}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function LegendChip({ status, count, t }) {
  const st = STATUS[status];
  const Icon = st.icon;
  return (
    <span data-testid={`legend-${status}`}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
          style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
      <Icon className="w-3.5 h-3.5" /> {t(st.labelKey)}
      <span className="ml-0.5 px-1.5 rounded-full bg-white/60 text-[10px] tabular-nums">{count}</span>
    </span>
  );
}
